import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import OpenApiClient from "./Api/OpenApiClient";
import { getQueriedProfileData, revalidateProfileDataQuery } from "./Api/Queries";
import { authTokenExists } from "./Api/Auth";
import { authStore } from "./Stores";
import { AppConfig } from "@app/Config";
import { joinURL } from "@app/Utils/Functions/urls";

export const defaultLocale = "en";

export const SUPPORTED_LANGUAGE_CODES = [
	"en",
	"de",
	"es",
	"fa",
	"fr",
	"it",
	"ja",
	"ko",
	"pt-br",
	"ru",
	"tr",
	"zh",
	"uk",
	"pl"
];

const languageDetector = new LanguageDetector();
languageDetector.init({
	order: ["querystring", "cookie", "localStorage", "sessionStorage", "navigator", "htmlTag", "path", "subdomain"]
});

export const getDetectedLanguage = (): string => {
	const detectedLangs = languageDetector.detect();
	const detectedLang = detectedLangs && detectedLangs.length > 0 ? detectedLangs[0] : "en";
	return SUPPORTED_LANGUAGE_CODES.includes(detectedLang as string) ? (detectedLang as string) : "en";
};

// Function to change language
export const changeLanguage = async (lang: string): Promise<void> => {
	if (SUPPORTED_LANGUAGE_CODES.includes(lang)) {
		dayjs.locale(lang);
	} else {
		log.warn("Locale is not supported: %s", lang);
		return;
	}
};

// Configure dayjs
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.locale(getDetectedLanguage());

// Формируем базовый путь к файлам локализации.
const basePath = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/locales/{{lng}}/translation.json`;

// Добавляем параметр для сброса кеша (cache-busting).
// Мы используем `VITE_APP_BUILD_ID`, который устанавливается во время сборки.
// Это гарантирует, что после каждого нового деплоя браузер запросит свежую версию файлов локализации,
// а не будет использовать старую из кеша.
const buildId = import.meta.env.VITE_APP_BUILD_ID;
const loadPath = buildId ? `${basePath}?v=${buildId}` : basePath;

void i18next
	.use(languageDetector)
	.use(Backend)
	.use(initReactI18next)
	.init({
		// lng: "en",
		fallbackLng: "en",
		debug: process.env.NODE_ENV === "development",
		load: "languageOnly",
		interpolation: {
			escapeValue: false
		},
		react: {
			transKeepBasicHtmlNodesFor: ["br", "strong", "b", "i", "p"]
		},
		backend: {
			loadPath: loadPath
		}
	});

// Initialize auth language
authStore.getState().updateAuthLanguage(i18next.resolvedLanguage || "en");

i18next.on("languageChanged", async (newLang) => {
	await changeLanguage(newLang);

	authStore.getState().updateAuthLanguage(newLang);

	if (!authTokenExists()) {
		log.warn("User not logged in. Skipping saving language change");
		return;
	}

	const profileData = await getQueriedProfileData();
	if (profileData.Locale !== newLang) {
		await OpenApiClient.Profile.fxIdWebFeaturesProfileUpdateProfileSettingsEndpoint({
			Language: newLang,
			DUMMY: false
		});
		void revalidateProfileDataQuery();
	}
});

export default i18next;
